import api from 'api';
import useAuth from 'hooks/useAuth';
import useCompanyConfig from 'hooks/useCompanyConfig';
import {get} from 'lodash';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {QueryFunctionContext, useQuery} from 'react-query';
import {CompanyType, ListResponse} from 'types';
import {CompanyProperty, CreateProperty} from 'types/property';

type FormValues = {
  name: string;
  email: string;
  address: string;
  phone: string;
  id: string;
  website: string;
  type: CompanyType;
};

export type CompanyPropertyForm = Omit<CompanyProperty, 'landlord'> & {
  landlord: number;
};

async function getCompanyProperties({queryKey}: QueryFunctionContext<any[]>) {
  const [, page] = queryKey;

  const {data} = await api.get<ListResponse<CompanyProperty>>(`/properties/`, {
    params: {
      limit: 20,
      page: +page * 20,
    },
  });

  return data;
}

async function getLandlordList() {
  const {data} = await api.get<ListResponse<CompanyProperty['landlord']>>(
    '/users/',
    {
      params: {role: 'landlord'},
    }
  );

  return data;
}

const useCompanyDetails = () => {
  const {currentUser} = useAuth();
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(
    sessionStorage.getItem('details_tab') || ''
  );
  const {hasFeature} = useCompanyConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: properties,
    refetch,
    isLoading,
  } = useQuery(['properties', currentPage - 1], getCompanyProperties);
  const {data: landlordList} = useQuery(['landlords'], getLandlordList, {
    enabled: hasFeature('hasManagedPropertiesOnCompanyDetails'),
  });
  const {
    register,
    formState: {errors},
    handleSubmit,
    setValue,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      name: get(currentUser, 'company.name', ''),
      email: get(currentUser, 'company.email', ''),
      address: get(currentUser, 'company.address', ''),
      phone: get(currentUser, 'company.phone', '')
        ? '(' +
          get(currentUser, 'company.phone', '').substring(0, 3) +
          ') ' +
          get(currentUser, 'company.phone', '').substring(3, 6) +
          '-' +
          get(currentUser, 'company.phone', '').substring(6, 10)
        : '',
      id: get(currentUser, 'company.id', ''),
      type: get(currentUser, 'company.type'),
      website: get(currentUser, 'company.website', ''),
    },
  });

  const saveHandler = async (data: FormValues) => {
    try {
      setLoading(true);
      const company_id = get(currentUser, 'company.id', 0);
      await api.put(`/companies/companies/${company_id}/`, {
        ...data,
        phone: data.phone.replace(/\D/g, ''),
      });
      setLoading(false);
      toast.success('Company details updated');
    } catch (error) {
      setLoading(false);
      toast.error(get(error, 'response.data.error_message', '') || 'Error');
    }
  };

  const sendConfirmation = async () => {
    try {
      await api.post('/companies/send-code/', {
        email: getValues('email'),
      });
      toast.success('Confirmation code sent to your email');
      setConfirmationVisible(true);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
    }
  };

  const confirmHandler = async (value: string) => {
    try {
      await api.post('/companies/verify-code/', {
        code: value,
        email: getValues('email'),
      });

      handleSubmit(saveHandler)();
      setConfirmationVisible(false);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
      setLoading(false);
    }
  };

  const handleCheckChanges = async () => {
    try {
      if (getValues('email') === currentUser?.company?.email) {
        handleSubmit(saveHandler)();
      } else {
        await sendConfirmation();
      }
    } catch (error) {}
  };

  async function createProperty(property: CreateProperty) {
    const data = await api.post<CompanyPropertyForm>(`/properties/`, {
      ...property,
    });
    if (data.status === 200) {
      await refetch();
    }
    return data;
  }
  async function deleteProperty(propertyId: number | undefined) {
    const {data} = await api.delete<any>(`/properties/${propertyId}/`);

    refetch();

    return data;
  }

  async function updateProperty(
    propertyId: number | undefined,
    body: CompanyPropertyForm
  ) {
    const {data} = await api.put<any>(`/properties/${propertyId}/`, body);

    refetch();
    return data;
  }

  useEffect(() => {
    setCurrentPage(1);
  }, [status]);
  return {
    isLoading,
    properties: properties?.results,
    landlordList: landlordList?.results || [],
    refetch,
    status,
    setStatus,
    currentUser,
    confirmationVisible,
    setConfirmationVisible,
    loading,
    errors,
    createProperty,
    deleteProperty,
    updateProperty,
    setValue,
    register,
    getValues,
    handleCheckChanges: handleSubmit(handleCheckChanges),
    confirmHandler,
    sendConfirmation,
  };
};

export default useCompanyDetails;
